import { MediaFileCampaign } from 'models/MediaFileCampaign';
import React, { useCallback, useState, useMemo } from 'react';
// @ts-ignore
import { useDrag, useDrop } from 'react-dnd';
import { Input } from 'reactstrap';
import { useApiPut, useInvalidateQuery, useApiDelete } from 'hooks/useApi';
import { useToast } from 'hooks/useToast';
import Button from 'components/Button';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import useUser from 'hooks/useUser';
import Icon from 'components/Icon';
import { startsWith } from 'lodash';
import Modal from 'components/Modal';
import ToastType from 'enum/ToastType';

type CampaignItemProps = {
  dragAndDropKeyAccept: string;
  mediaCampaign: MediaFileCampaign;
  campaignId: string;
  index: number;
  onMediaReOrderFinish: () => void;
  onMediaReOrderPreview: (dragId: string, hoverId: string) => void;
  onClick?: (mediaCampaign: MediaFileCampaign) => void;
  isFirst?: boolean;
  isLast?: boolean;
  moveUp: (mediaId: string) => void;
  moveDown: (mediaId: string) => void;
};

type DragAndDropOrderType = { mediaId: string };
type DragOrderPreviewType = { isDragging: boolean };

const CampaignPositionItem = ({
  mediaCampaign,
  onMediaReOrderFinish,
  onMediaReOrderPreview,
  dragAndDropKeyAccept,
  onClick,
  campaignId,
  index,
  isFirst,
  isLast,
  moveUp,
  moveDown
}: CampaignItemProps) => {
  const user = useUser();
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editedTime, setEditedTime] = useState(mediaCampaign.duration || 0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutate, isPending } = useApiPut<MediaFileCampaign>(
    `/media-manager/campaigns/${campaignId}/medias/${mediaCampaign.id}`
  );
  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    `/media-manager/campaigns/${campaignId}/medias/${mediaCampaign.id}`
  );
  const userCanEdit = useMemo(() => {
    return user?.resources?.[TypeResource.CAMPAIGNS]?.includes(
      TypeAction.UPDATE
    );
  }, [user]);

  const userCanDelete = useMemo(() => {
    return user?.resources?.[TypeResource.CAMPAIGNS]?.includes(
      TypeAction.DELETE
    );
  }, [user]);

  const handleTimeClick = useCallback(
    (duration: number) => {
      if (!userCanEdit) return;
      setIsEditingTime(true);
      setEditedTime(duration);
    },
    [userCanEdit]
  );
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTime(parseInt(e.target.value));
  };

  const { addToast } = useToast();

  const { invalidate } = useInvalidateQuery(['/media-manager/campaigns']);

  const handleTimeSave = useCallback(() => {
    mutate(
      {
        duration: editedTime,
        order: mediaCampaign.order,
        active: mediaCampaign.active
      },
      {
        onSuccess: () => {
          invalidate();
          addToast(ToastType.SUCCESS, 'Tempo atualizado com sucesso.');
        },
        onError: (error: any) => {
          const { errors } = error || ({} as any);
          const message =
            errors?.status === 422 ? errors?.detail?.duration : errors?.detail;
          addToast(ToastType.ERROR, message || 'Erro ao atualizar o tempo.');
        }
      }
    );
    setIsEditingTime(false);
  }, [
    editedTime,
    mutate,
    mediaCampaign.order,
    mediaCampaign.active,
    addToast,
    invalidate
  ]);

  const handleTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTimeSave();
    } else if (e.key === 'Escape') {
      setIsEditingTime(false);
    }
  };

  const [{ isDragging }, drag] = useDrag<
    DragAndDropOrderType,
    DragAndDropOrderType,
    DragOrderPreviewType
  >(() => ({
    type: dragAndDropKeyAccept,
    item: { mediaId: mediaCampaign.id! },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }));

  const [, drop] = useDrop<DragAndDropOrderType>(
    {
      accept: dragAndDropKeyAccept,
      hover(item) {
        onMediaReOrderPreview(item.mediaId, mediaCampaign.id!);
      },
      drop() {
        onMediaReOrderFinish();
      }
    },
    [onMediaReOrderPreview, mediaCampaign.id, onMediaReOrderFinish]
  );

  const handleMediaClick = useCallback(() => {
    onClick?.(mediaCampaign);
  }, [mediaCampaign, onClick]);

  const handleOnDelete = useCallback(() => {
    if (!userCanDelete) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(ToastType.ERROR, 'Erro ao excluir a mídia.');
      },
      onSuccess: () => {
        invalidate();
        addToast(ToastType.SUCCESS, 'Mídia excluída com sucesso');
      }
    });
  }, [addToast, mutateDelete, userCanDelete, invalidate]);

  const handleMoveUp = useCallback(() => {
    moveUp(mediaCampaign.id!);
    onMediaReOrderFinish();
  }, [moveUp, mediaCampaign.id, onMediaReOrderFinish]);

  const handleMoveDown = useCallback(() => {
    moveDown(mediaCampaign.id!);
    onMediaReOrderFinish();
  }, [moveDown, mediaCampaign.id, onMediaReOrderFinish]);

  return (
    <div ref={drop}>
      <div ref={drag}>
        <div>
          {!isDragging ? (
            <div className="d-flex justify-content-between align-items-center border-top">
              <div>
                <div className="d-flex flex-column">
                  {isFirst ? null : (
                    <Button
                      color="transparent"
                      type="button"
                      className="border-0 p-0 button-order text-primary"
                      icon="chevron-up"
                      onClick={handleMoveUp}
                    />
                  )}
                  {isLast ? null : (
                    <Button
                      color="transparent"
                      type="button"
                      className="border-0 p-0 button-order text-primary"
                      icon="chevron-down"
                      onClick={handleMoveDown}
                    />
                  )}
                </div>
              </div>
              <div>
                <small>Ordem de execução </small>
                <br />
                <b>{index + 1}</b>
              </div>
              <div>
                <small>Tempo </small>
                <br />
                {startsWith(mediaCampaign.mediaFile?.mineType, 'image') &&
                  (!isEditingTime ? (
                    <span
                      role="button"
                      onClick={() =>
                        handleTimeClick(mediaCampaign?.duration || 0)
                      }
                    >
                      <b>{mediaCampaign?.duration} s</b>
                      {userCanEdit && (
                        <Icon icon="pencil" className="ms-1 text-primary" />
                      )}
                    </span>
                  ) : (
                    <span>
                      <Input
                        className="w-50"
                        type="number"
                        value={editedTime}
                        onChange={handleTimeChange}
                        onKeyDown={handleTimeKeyDown}
                      />
                      <span className="d-flex justify-content-end w-50">
                        <Button
                          type="button"
                          className="mt-1 ps-1 pb-1 pt-0 pe-0 text-primary bg-transparent border-0"
                          icon="check-lg"
                          loading={isPending}
                          onClick={handleTimeSave}
                        ></Button>
                        <Button
                          type="button"
                          className="mt-1 ms-1 ps-1 pb-1 pt-0 pe-0 text-danger bg-transparent border-0"
                          icon="x-lg"
                          onClick={() => setIsEditingTime(false)}
                        ></Button>
                      </span>
                    </span>
                  ))}
                {startsWith(mediaCampaign.mediaFile?.mineType, 'video') && (
                  <b>{mediaCampaign?.duration} s</b>
                )}
              </div>
              <div>
                <div
                  role="button"
                  onClick={handleMediaClick}
                  className="img-thumbnail border-0"
                >
                  <img
                    alt={mediaCampaign.mediaFile?.name}
                    src={mediaCampaign.thumbnail}
                    className="m-auto d-block"
                    width={100}
                  />
                </div>
              </div>
              <div>
                {userCanDelete && (
                  <Button
                    type="button"
                    loading={isDeleting}
                    icon="trash"
                    className="ms-4 border-0 pe-0 button-delete text-danger"
                    onClick={() => setShowDeleteConfirmation(true)}
                  ></Button>
                )}
              </div>
            </div>
          ) : (
            <div className="campaign-medias-list-item">
              <div className="bg-dark-subtle"></div>
            </div>
          )}
        </div>
        <Modal
          primaryButtonAction={handleOnDelete}
          primaryButtonLabel="Excluir"
          title="Atenção!"
          isOpen={showDeleteConfirmation}
          showCloseButton={false}
          toggle={() => setShowDeleteConfirmation((x) => !x)}
          secondaryButtonAction={() => setShowDeleteConfirmation(false)}
          secondaryButtonLabel="Cancelar"
        >
          <p>
            Desaja realmente excluir a mídia? <br />
            <small>Essa alteração não poderá ser revertida.</small>
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default CampaignPositionItem;
